/**
 * Périmètre de simulateurs d'une licence d'ABONNEMENT attribuée à une personne (Starter, Focus,
 * Premium) — règle unique, partagée client + serveur (aucune dépendance).
 *
 * Une licence d'abonnement donne droit à un nombre de simulateurs (`licences.max_simulators` :
 * Starter 1, Focus 3, Premium 0 = tous). Le choix est PERSONNEL : il vit dans
 * `user_licences.simulator_ids`. Un abonnement Zoho le posait déjà à l'attribution d'un siège
 * (`handleAssignSeat`) ; une licence attribuée depuis le POOL du compte (abonnement réglé par
 * virement, posé à la main par un ADM) ne le posait pas : la personne retombait sur l'ancien
 * rattachement `simulator_licences` (un seul simulateur, ArguWin) sans pouvoir choisir.
 * Incident ALTIOS du 14/10/2026.
 *
 * Hors périmètre : un pack one-shot (droit de NIVEAU COMPTE, jamais attribué à une personne) et
 * les licences « simulateur » historiques (sans `tier`), rattachées par `simulator_licences`.
 */

export type LicenceSimulatorChoice =
  | { kind: "none" }
  | { kind: "all" }
  | { kind: "choose"; max: number };

export interface LicenceForSimulatorChoice {
  isOneShot?: boolean | null;
  tier?: string | null;
  maxSimulators?: number | null;
}

/**
 * Niveaux d'ABONNEMENT. Tout autre `tier` en est exclu : le module additionnel « Sales Coach Réel »
 * (`SALES_COACH_REEL_ADDON`, tier `addon`, `max_simulators` = 1) ouvre son coach par
 * `simulator_licences` — le traiter en « 1 simulateur à choisir » aurait offert n'importe quel
 * simulateur avec le module (relevé en prod et en demo le 14/10/2026, avant l'envoi).
 */
export const SUBSCRIPTION_TIERS: readonly string[] = ["starter", "focus", "premium"];

/** Ce que l'attribution de cette licence demande : rien, tous les simulateurs, ou un choix borné. */
export function licenceSimulatorChoice(lic: LicenceForSimulatorChoice | null | undefined): LicenceSimulatorChoice {
  if (!lic || lic.isOneShot || !lic.tier || !SUBSCRIPTION_TIERS.includes(lic.tier) || lic.maxSimulators == null) return { kind: "none" };
  if (lic.maxSimulators === 0) return { kind: "all" };
  return { kind: "choose", max: lic.maxSimulators };
}

export type SimulatorChoiceRefusal =
  | { code: "licence_simulators_required"; params: { max: number } }
  | { code: "licence_simulators_too_many"; params: { max: number } }
  | { code: "licence_simulators_unknown" };

/**
 * Contrôle d'un choix de simulateurs pour une licence « à choisir » : au moins un, au plus `max`,
 * tous parmi les simulateurs proposables. Doublons ignorés. Renvoie la liste retenue ou le refus.
 */
export function checkSimulatorChoice(
  choice: { kind: "choose"; max: number },
  simulatorIds: unknown,
  selectableIds: Iterable<string>,
): { ok: true; simulatorIds: string[] } | { ok: false; refusal: SimulatorChoiceRefusal } {
  const ids = Array.isArray(simulatorIds)
    ? [...new Set(simulatorIds.filter((id): id is string => typeof id === "string" && id.length > 0))]
    : [];
  if (ids.length === 0) return { ok: false, refusal: { code: "licence_simulators_required", params: { max: choice.max } } };
  if (ids.length > choice.max) return { ok: false, refusal: { code: "licence_simulators_too_many", params: { max: choice.max } } };
  const selectable = new Set(selectableIds);
  if (ids.some((id) => !selectable.has(id))) return { ok: false, refusal: { code: "licence_simulators_unknown" } };
  return { ok: true, simulatorIds: ids };
}
